import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetDiary, apiCreateDiaryEntry, apiDeleteDiaryEntry } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, BookOpen, LogOut, Smile, Frown, Meh, Heart, Calendar } from "lucide-react";
import { motion } from "framer-motion";

const MOODS = [
  { value: "felice", label: "Felice", icon: Smile, color: "text-amber-500" },
  { value: "sereno", label: "Sereno", icon: Heart, color: "text-emerald-500" },
  { value: "neutro", label: "Neutro", icon: Meh, color: "text-blue-400" },
  { value: "triste", label: "Triste", icon: Frown, color: "text-violet-400" },
];

export default function DiaryPage() {
  const { token, user, logout } = useAuth();
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("");
  const [saving, setSaving] = useState(false);

  const loadEntries = async () => {
    try {
      const data = await apiGetDiary(token);
      setEntries(data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadEntries(); }, [token]);

  const handleCreate = async () => {
    if (!content.trim()) { toast.error("Scrivi qualcosa"); return; }
    setSaving(true);
    try {
      await apiCreateDiaryEntry(token, { title, content, mood });
      toast.success("Pensiero salvato");
      setTitle(""); setContent(""); setMood(""); setDialogOpen(false);
      loadEntries();
    } catch { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteDiaryEntry(token, id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      toast.success("Voce eliminata");
    } catch { toast.error("Errore"); }
  };

  const moodIcon = (value) => {
    const m = MOODS.find((x) => x.value === value);
    if (!m) return null;
    return <m.icon className={`w-3.5 h-3.5 ${m.color}`} />;
  };

  return (
    <div className="min-h-screen bg-gradient-hero" data-testid="diary-page">
      {/* Top bar */}
      <header className="border-b border-border/40 bg-card/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="diary-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} className="h-8 w-8 text-muted-foreground">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-1">Il mio Diario</h1>
            <p className="text-sm text-muted-foreground">I tuoi pensieri, per chi verra dopo di te.</p>
          </motion.div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="premium" size="default" data-testid="new-entry-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Scrivi
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-xl">Nuovo pensiero</DialogTitle>
                <DialogDescription>Scrivi un pensiero o un ricordo da conservare per sempre.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label>Titolo (opzionale)</Label>
                  <Input placeholder="Un titolo per questo pensiero..." value={title} onChange={(e) => setTitle(e.target.value)}
                    data-testid="entry-title" />
                </div>
                <div className="space-y-2">
                  <Label>Il tuo pensiero *</Label>
                  <Textarea placeholder="Scrivi quello che senti, quello che vuoi lasciare..." value={content}
                    onChange={(e) => setContent(e.target.value)} rows={5} data-testid="entry-content" />
                </div>
                <div className="space-y-2">
                  <Label>Come ti senti?</Label>
                  <div className="flex gap-2">
                    {MOODS.map((m) => (
                      <button key={m.value} onClick={() => setMood(mood === m.value ? "" : m.value)}
                        data-testid={`mood-${m.value}`}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${
                          mood === m.value
                            ? "border-primary bg-primary/5 text-foreground shadow-sm"
                            : "border-border/60 text-muted-foreground hover:border-primary/30"
                        }`}>
                        <m.icon className={`w-4 h-4 ${m.color}`} /> {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button variant="premium" className="w-full" onClick={handleCreate} disabled={saving} data-testid="save-entry-btn">
                  {saving ? "Salvataggio..." : "Salva pensiero"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Entries */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <Card key={i} className="bg-card border-border/20 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-secondary rounded w-32 mb-3" />
                  <div className="h-3 bg-secondary rounded w-full mb-2" />
                  <div className="h-3 bg-secondary rounded w-3/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="bg-card border-border/30 border-dashed shadow-soft" data-testid="empty-diary">
              <CardContent className="p-10 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/6 flex items-center justify-center mx-auto mb-5">
                  <BookOpen className="w-7 h-7 text-primary/30" />
                </div>
                <h3 className="font-serif text-base font-bold text-foreground mb-2">Il tuo diario e vuoto</h3>
                <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                  Inizia a scrivere i tuoi pensieri, storie e ricordi. Saranno custoditi per sempre.
                </p>
                <Button variant="outline-primary" onClick={() => setDialogOpen(true)} data-testid="write-first-btn">
                  <Plus className="w-4 h-4 mr-2" /> Scrivi il primo pensiero
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry, i) => (
              <motion.div key={entry.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="bg-card border-border/30 shadow-soft hover:shadow-medium transition-shadow duration-300"
                  data-testid={`diary-entry-${entry.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {entry.title && (
                          <h3 className="font-serif text-base font-bold text-foreground mb-1.5 truncate">{entry.title}</h3>
                        )}
                        <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap line-clamp-4">{entry.content}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(entry.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
                          </span>
                          {entry.mood && (
                            <span className="flex items-center gap-1 text-[11px]">
                              {moodIcon(entry.mood)}
                              <span className="text-muted-foreground capitalize">{entry.mood}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <button onClick={() => handleDelete(entry.id)}
                        className="text-muted-foreground/25 hover:text-destructive transition-colors flex-shrink-0 mt-1"
                        data-testid={`delete-entry-${entry.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
